/* ==========================================================================
   ENQUIRY DRAWER — #182, on Web3Forms with the ADVANCED file uploader
   ==========================================================================

   ── WHY THE ADVANCED UPLOADER, AND WHY I GOT THIS WRONG FIRST ────────────
   I said the advanced uploader "gives links, not attachments" and built on
   the default one instead. That was inference from a React Hook Form
   snippet passing e.cdnUrl, stated as though it were fact. It is not what
   the vanilla uploader does: that one PUTs to Web3Forms' OWN presigned
   endpoint (api.web3forms.com/upload), so the file lands in their storage
   and their mailer can attach it.

   Their own File Attachments page contradicts itself, which did not help:
   it shows two named inputs under "Multiple File Attachments", then says
   two paragraphs later that the default uploader "works only for single
   files" and "does not support multiple files", recommending the advanced
   one. We built the documented-but-disclaimed path and got nothing.

   ── THE TRAP IN SWITCHING ────────────────────────────────────────────────
   The two uploaders need OPPOSITE submit code, and mixing them drops files
   without an error:

     default  → FormData, and explicitly NO headers ("the browser auto-adds
                them" — setting Content-Type strips the multipart boundary)
     advanced → JSON, and Content-Type: application/json REQUIRED

   This file does the second. The files are not in the form at submit time;
   FilePond has already uploaded them and left references behind.

   ── THE RULE THIS FILE ENFORCES ──────────────────────────────────────────
   Nothing is reported as sent unless Web3Forms says so. An enquiry that
   arrives without its attachment, silently, is worse than one that fails
   loudly — the customer believes we have their venue plan and we do not.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-enquiry-drawer]');
  if (!root) return;

  var form     = root.querySelector('[data-ed-form]');
  var panel    = root.querySelector('.ed__panel');
  var picker   = root.querySelector('[data-ed-branch-picker]');
  var known    = root.querySelector('[data-ed-branch-known]');
  var knownName= root.querySelector('[data-ed-branch-name]');
  var branchVal= root.querySelector('[data-ed-branch-value]');
  var rest     = root.querySelector('[data-ed-rest]');

  /* Captured once, before anything can clear it. This is what Liquid wrote
     from cart.attributes['Location'] — the booking bar's own answer. */
  var draftNote = root.querySelector('[data-ed-draft-note]');
  /* Set only by a tap on the picker. Everything else — the cart, the
     contract, the draft — leaves it false. */
  var chosenByCustomer = false;

  var serverBranch = (root.querySelector('[data-ed-branch-value]') || {}).value || '';

  var wait     = root.querySelector('[data-ed-wait]');
  var dateInput= root.querySelector('[data-ed-date]');
  var dateValue= root.querySelector('[data-ed-date-value]');
  var pageField= root.querySelector('[data-ed-page]');
  var errorBox = root.querySelector('[data-ed-error]');
  var sendBtn  = root.querySelector('[data-ed-send]');
  var doneBox  = root.querySelector('[data-ed-done]');
  var doneText = root.querySelector('[data-ed-done-text]');
  var foot     = root.querySelector('.ed__foot');

  var sending = false;
  var lastFocus = null;

  /* ---------------------------------------------------------------------
     THE BRANCH

     Read from the booking bar if it has resolved one. Same contract as the
     call button in the mega menu (#185): window.DJW_BRANCH, or
     data-djw-branch on <html>, either carrying a location handle. Neither
     exists yet, so today the picker always shows — which is the correct
     default and the state that needed designing.
     --------------------------------------------------------------------- */

  /* A BUG THAT WAS WAITING FOR THE BOOKING BAR, not a new feature.

     The contract publishes a location HANDLE — "sydney" — because that is
     what the mega menu's call button matches against. This function
     returned it raw, and setBranch() puts its argument straight into the
     visible sentence and the submitted value. So the day the booking bar
     starts publishing, the drawer would have read "we'll send your enquiry
     to our sydney branch" and submitted Branch=sydney.

     Nothing surfaced it because nothing publishes yet. It resolves the
     handle against the picker buttons, which already carry both. */
  function resolvedBranch() {
    var g = typeof window.DJW_BRANCH === 'string' ? window.DJW_BRANCH : '';
    var a = document.documentElement.getAttribute('data-djw-branch') || '';
    var handle = (g || a || '').trim().toLowerCase();
    if (!handle) return '';

    var match = null;
    [].forEach.call(root.querySelectorAll('[data-ed-handle]'), function (b) {
      if ((b.dataset.edHandle || '').toLowerCase() === handle) match = b;
    });

    /* An unrecognised handle falls back to the picker rather than guessing.
       A branch the drawer cannot name is one it should be asking about. */
    return match ? match.dataset.edBranch : '';
  }

  function setBranch(name) {
    branchVal.value = name || '';

    /* The rest of the form, and the footer with it — here rather than on
       the picker's click handler, because the buttons are not the only way
       in. The cart attribute, the djw:branch-change event and the Change
       link all arrive through this function. */
    if (rest) rest.hidden = !name;
    if (wait) wait.hidden = !!name;
    if (foot) foot.hidden = !name;

    if (name) {
      knownName.textContent = name;
      known.hidden = false;
      picker.hidden = true;
    } else {
      known.hidden = true;
      picker.hidden = false;
      [].forEach.call(picker.querySelectorAll('[data-ed-branch]'), function (b) {
        b.removeAttribute('data-chosen');
      });
    }
  }

  [].forEach.call(root.querySelectorAll('[data-ed-branch]'), function (btn) {
    btn.addEventListener('click', function () {
      [].forEach.call(picker.querySelectorAll('[data-ed-branch]'), function (b) {
        b.removeAttribute('data-chosen');
      });
      btn.setAttribute('data-chosen', 'true');

      /* setBranch owns every consequence of having a branch — the value,
         the strip, the picker, the form, the footer. This handler owns
         only the two things that are true of a TAP and not of a cart
         read: it was the customer's choice, and it should survive a
         navigation. Duplicating the rest here is what produced two
         different-looking versions of the same state. */
      chosenByCustomer = true;
      setBranch(btn.dataset.edBranch);
      saveDraft();          // persist before they can navigate away
      clearError();
    });
  });

  root.querySelector('[data-ed-branch-change]').addEventListener('click', function () {
    /* Change reopens the question, so the customer has un-chosen — the bar
       is allowed to answer again. */
    chosenByCustomer = false;
    setBranch('');
    saveDraft();          // writes _chosen: 0, or clears a branch-only draft
  });

  /* THE LISTENER, #185. The branch was read once, on open — so a customer
     who opened the drawer and then picked a branch in the booking bar saw
     the question twice.

     Only while the drawer is OPEN and only while the customer has not
     already answered: overwriting a deliberate choice with a background
     event is worse than asking. */
  /* 'bookingbar:changed', not the invented 'djw:branch-change' — #185.
     Nothing ever dispatched the latter, so this listener has been inert
     since it was written, and syncBranchFromCart on open was carrying the
     whole job alone.

     THREE PAYLOADS, and the third is why this cannot just read
     detail.locationGid:

       { locationGid, startDate, endDate }             a change
       { locationGid, startDate, endDate, fromCart }   page-load restore
       { cleared: true }                               booking cleared

     On 'cleared' the branch goes back to unanswered and the picker
     returns — leaving a stale branch selected after someone cleared their
     booking would submit an enquiry to a warehouse they are no longer
     dealing with.

     The customer's own choice still wins over all three. */
  document.addEventListener('bookingbar:changed', function (e) {
    if (chosenByCustomer) return;
    var d = (e && e.detail) || {};

    if (d.cleared) {
      if (root.dataset.open === 'true') setBranch('');
      return;
    }

    var name = nameFromGid(d.locationGid);
    if (!name) return;
    if (root.dataset.open !== 'true') {
      /* Closed: remember it for the next open rather than writing into a
         drawer nobody is looking at. syncBranchFromCart would get there
         eventually, but only after the cart write lands. */
      serverBranch = name;
      return;
    }
    setBranch(name);
  });

  /* The event carries a GID; every other source here carries the location
     NAME, because that is what the cart attribute holds.
     window.locationMetaobjects bridges them and is already on the page. */
  function nameFromGid(gid) {
    if (!gid) return '';
    var bare = String(gid).replace('gid://shopify/Metaobject/', '');
    var all = window.locationMetaobjects || {};
    for (var handle in all) {
      var entry = all[handle] || {};
      var id = String(entry.gid || '').replace('gid://shopify/Metaobject/', '');
      if (id && id === bare) return entry.name || '';
    }
    return '';
  }

  /* ---------------------------------------------------------------------
     THE DATE

     The visible input is type="date" and carries no name; this fills the
     hidden q7_eventDate with DD-MM-YYYY. Two fields for one answer, because
     a native picker only speaks ISO and this project's payloads are
     DD-MM-YYYY — see the note in the snippet.

     Derived on input rather than read at submit time, so the two can never
     be out of step if something else reads the hidden value first.
     --------------------------------------------------------------------- */

  if (dateInput && dateValue) {
    dateInput.addEventListener('input', function () {
      var v = dateInput.value;                       // yyyy-mm-dd or ''
      if (!v) { dateValue.value = ''; return; }
      var p = v.split('-');
      dateValue.value = p[2] + '-' + p[1] + '-' + p[0];
    });
  }

  /* Re-read the cart on open. The page-load value is stale the moment the
     customer switches branch in the bar and keeps browsing, which is the
     ordinary sequence rather than an edge case.

     A failed fetch is not an error worth surfacing: the page-load value
     stands and it is almost always right. */
  function syncBranchFromCart() {
    if (chosenByCustomer) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var live = (cart && cart.attributes && cart.attributes.Location) || '';
        if (!live || chosenByCustomer) return;
        if (live === branchVal.value) return;
        if (root.dataset.open !== 'true') return;
        setBranch(live);
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------
     THE DRAFT

     sessionStorage rather than localStorage: a half-written enquiry should
     survive a walk through three product pages, not resurface in a
     fortnight carrying last month's event date. It dies with the tab.

     NOT PERSISTED: attachments. A File cannot be serialised, and a file
     input cannot be filled programmatically — so a restored draft has its
     text and none of its files. The drawer says so rather than letting
     someone discover the omission after sending.

     Also not persisted: access_key, Page, Source, Reference, botcheck.
     Three of those are regenerated per submission and the other two are
     not the customer's to carry.
     --------------------------------------------------------------------- */

  var DRAFT_KEY = 'djw:enquiry-draft';
  var SKIP = { access_key: 1, subject: 1, botcheck: 1, Page: 1, Source: 1, Reference: 1 };

  function readDraft() {
    try {
      var raw = sessionStorage.getItem(DRAFT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (err) { return null; }
  }

  /* Only a branch the customer chose. A cart-derived one is re-read from
     the cart, which is fresher than anything in storage. */
  function draftChosenBranch() {
    var d = readDraft();
    return (d && d._chosen && d.Branch) ? d.Branch : '';
  }

  function saveDraft() {
    try {
      var out = {};
      var any = false;
      [].forEach.call(form.elements, function (el) {
        if (!el.name || SKIP[el.name] || el.type === 'file') return;
        if (!el.value) return;
        out[el.name] = el.value;
        if (el.name !== 'Branch') any = true;
      });

      /* The flag travels with the value. Without it a reload leaves a
         branch in storage with no way to tell whether the customer chose
         it or the cart supplied it. */
      out._chosen = chosenByCustomer ? 1 : 0;

      /* A lone branch is worth keeping ONLY when the customer chose it —
         that is the one thing the cart cannot tell us again. A lone branch
         from the cart is not a draft, and storing it would put the "we
         kept what you had written" note over an empty form. */
      if (!any && !chosenByCustomer) { sessionStorage.removeItem(DRAFT_KEY); return; }
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(out));
    } catch (err) { /* private mode, quota — a lost draft is not an error */ }
  }

  function clearDraft() {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (err) {}
  }

  function restoreDraft() {
    var d = readDraft();
    if (!d) return false;
    var filled = false;
    [].forEach.call(form.elements, function (el) {
      if (!el.name || SKIP[el.name] || el.type === 'file') return;
      if (typeof d[el.name] !== 'string') return;
      el.value = d[el.name];
      if (el.name !== 'Branch') filled = true;
    });
    /* ── THE PICKER IS REBUILT FROM ITS TWIN, dev-build 186 ─────────────
       The visible date picker has no name, so the loop above skips it and
       only the hidden DD-MM-YYYY twin comes back from storage.

       This block used to run the other way — twin from picker — on the
       belief that the picker had just been restored. It cannot have been, so
       the picker was always empty here, the condition was never true, and a
       restored draft showed a blank date over a filled hidden field. The
       picker is required, so the customer could not send until they picked
       the date again: the answer was there, and the form refused it. */
    if (dateInput && dateValue && dateValue.value) {
      var p = dateValue.value.split('-');                 // DD-MM-YYYY
      if (p.length === 3) dateInput.value = p[2] + '-' + p[1] + '-' + p[0];
    }
    return filled;
  }

  form.addEventListener('input', saveDraft);
  form.addEventListener('change', saveDraft);

  /* ---------------------------------------------------------------------
     OPEN / CLOSE
     --------------------------------------------------------------------- */

  function open() {
    lastFocus = document.activeElement;
    pageField.value = window.location.pathname + window.location.search;
    doneBox.hidden = true;
    form.hidden = false;
    foot.hidden = false;

    /* AFTER the resets above, not before. foot.hidden = false used to run
       two lines below this call and quietly undid the gate on every open. */
    /* THE CUSTOMER'S OWN ANSWER FIRST — and restoring it has to restore
       the flag too, or the cart sync a moment later would overwrite the
       choice this just put back. Then the live contract, then what the
       cart said at page load. */
    var mine = draftChosenBranch();
    if (mine) {
      chosenByCustomer = true;
      setBranch(mine);
    } else {
      chosenByCustomer = false;
      setBranch(resolvedBranch() || serverBranch);
    }
    clearError();
    if (restoreDraft() && draftNote) draftNote.hidden = false;
    syncBranchFromCart();
    root.dataset.open = 'true';
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    var first = form.querySelector('input:not([type=hidden]), textarea, button');
    if (first) first.focus();
  }

  function close() {
    root.dataset.open = 'false';
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  [].forEach.call(root.querySelectorAll('[data-ed-close]'), function (b) {
    b.addEventListener('click', close);
  });

  /* Escape closes — unless a send is in flight, because closing mid-upload
     would leave the customer with no idea whether it went. */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.dataset.open === 'true' && !sending) close();
  });

  /* Focus trap. A modal dialog that lets Tab escape into the page behind it
     is a dialog only for people using a mouse. */
  panel.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var f = panel.querySelectorAll('a[href], button:not([disabled]), input:not([type=hidden]), select, textarea');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Anything with data-open-enquiry opens it. The mega menu's Email us
     button carries that attribute — a checkbox on the block rather than a
     match on its title, for the reason #159 exists. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-open-enquiry]') : null;
    if (!t) return;
    e.preventDefault();
    open();
  });

  /* ---------------------------------------------------------------------
     ERRORS
     --------------------------------------------------------------------- */

  function showError(msg) {
    errorBox.textContent = msg;
    errorBox.hidden = false;
  }
  function clearError() {
    errorBox.textContent = '';
    errorBox.hidden = true;
  }

  /* ---------------------------------------------------------------------
     FILES

     Nothing to do here any more. FilePond — loaded by Web3Forms' own client
     script — owns the control, the drag and drop, the per-file size limit
     and the upload. Its markup replaces the hidden input in the snippet and
     it leaves the uploaded file references behind in the form.

     The three hand-built slots are gone with it. They existed only to work
     around the default uploader's one-file-per-input limit.
     --------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------
     THE SUBMISSION — JSON, not FormData

     Their advanced-uploader docs: "For Javascript usage, you must serialize
     the data and include Content-Type headers as application/json." That is
     the exact opposite of the default uploader's rule, and getting it the
     wrong way round is how attachments vanish without an error.
     --------------------------------------------------------------------- */

  function formToObject(fd) {
    var out = {};
    var stray = null;

    fd.forEach(function (value, key) {
      /* A File still in the form means FilePond did NOT run — its script
         failed to load, or it never bound. JSON.stringify turns that into
         {} and sends a cheerful enquiry with the venue plan missing.
         Caught and refused instead. */
      if (value instanceof File) {
        if (value.name) stray = value.name;
        return;
      }

      /* Several files leave several entries under one name.
         Object.fromEntries, which their example uses, keeps only the LAST —
         so three attachments would arrive as one. Collected into an array. */
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        if (!Array.isArray(out[key])) out[key] = [out[key]];
        out[key].push(value);
      } else {
        out[key] = value;
      }
    });

    return { data: out, stray: stray };
  }

  sendBtn.addEventListener('click', function () {
    if (sending) return;
    clearError();

    if (!branchVal.value) {
      showError('Please choose which warehouse this is for.');
      var b = picker.querySelector('[data-ed-branch]');
      if (b) b.focus();
      return;
    }

    if (!form.reportValidity()) return;

    var built = formToObject(new FormData(form));

    /* ── THE REFERENCE ────────────────────────────────────────────────
       Front threads by subject and participants, so two enquiries from
       the same customer with the same subject land in one conversation.
       A unique code in the subject is what keeps them apart.

       The subject line itself is composed in the Web3Forms form settings,
       where {field:Reference} interpolates this value. The drawer's only
       job is to supply it — it does not set the subject.

       WHAT THIS IS NOT: it is generated in the browser, so it is not
       authoritative and cannot be trusted as a booking reference. A real
       one has to come from the order. This is a thread separator — if it
       ever needs to mean something, it has to be minted server-side.

       THE ALPHABET has no 0, O, 1, I, L or U, because support will read
       these down a phone. Five characters come from the low digits of the
       clock, which cycle about every seven hours; three are random.

       SIZED BY TEST, not by argument. With two random characters, 100 refs
       generated inside a single millisecond came back 93 unique — only the
       tail varies when the clock is fixed, and two characters is 900
       combinations. Three makes it 27,000 and the same test returns 100.
       Real submissions arrive seconds apart, where the clock digits differ
       anyway, so this is insurance rather than necessity. */
    var ref = (function () {
      var A = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
      var t = Date.now();
      var out = '';
      for (var i = 0; i < 5; i++) { out = A.charAt(t % A.length) + out; t = Math.floor(t / A.length); }
      for (var j = 0; j < 3; j++) { out += A.charAt(Math.floor(Math.random() * A.length)); }
      return 'FE-' + out;
    })();

    built.data.Reference = ref;


    /* Never send an empty Phone — see the note above. The email template
       has no way to omit the row, so the row is given something true to
       say instead. */
    if (!String(built.data.Phone || '').trim()) {
      built.data.Phone = 'Not provided';
    }

    if (built.stray) {
      showError('Your attachment did not finish uploading. Please remove it and add it again, or send without it and reply to our email.');
      return;
    }

    /* Better to refuse than to look like it sent. */
    if (!built.data.access_key || String(built.data.access_key).indexOf('PASTE') !== -1) {
      showError('This form is not connected yet. Please call us instead.');
      return;
    }

    sending = true;
    sendBtn.disabled = true;
    sendBtn.textContent = 'Sending\u2026';

    fetch(form.action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(built.data)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok || json.success === false) {
            throw new Error(json.message || ('status ' + res.status));
          }
          return json;
        });
      })
      .then(function () {
        var name = branchVal.value;
        doneText.textContent = name
          ? 'The ' + name + ' team has received your enquiry. We do our best to reply within 24 hours.'
          : 'We have received your enquiry. We do our best to reply within 24 hours.';
        clearDraft();
        form.hidden = true;
        foot.hidden = true;
        doneBox.hidden = false;
      })
      .catch(function () {
        showError('That did not send. Please try again, or call us if it keeps failing.');
      })
      .then(function () {
        sending = false;
        sendBtn.disabled = false;
        sendBtn.textContent = 'Send enquiry';
      });
  });
})();
